import {
  CloseRounded,
  MoreVert,
  PlayCircleFilled,
  QueryBuilderRounded,
  ReplayRounded,
  Visibility,
  VisibilityOff
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  styled
} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import clsx from 'clsx'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { courseLessonStep } from '../../../../../redux/actions/courses'
import { deleteLesson, setViewLesson, showLesson } from '../../../../../redux/actions/lessons'
import { IRootState } from '../../../../../redux/types'
import CustomSnackBar from '../../../../shared/components/CustomSnackBar'
import ScreenLoading from '../../../../shared/components/ScreenLoading'
import { CancelButton, DeleteButton } from '../../../../shared/components/inputs'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import { StyledDialog } from '../../../../shared/styles/StyledDialog'
import { isAdmin, isTrainingManager } from '../../../users/utils/RolesHelper'

type LessonTemplateProps = {
  lesson: any
  hasaccess: boolean
  vipContent: boolean
}

export const LessonCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  background: '#F5F6F6',
  marginTop: theme.spacing(4),
  borderRadius: 16,
  boxShadow: 'none',
  '&.active': {
    border: '2px solid',
    borderColor: theme.palette.error.main,
    background: '#FAECF0'
  },
  '&:hover': {
    cursor: 'pointer',
    background: theme.palette.error.light
  },
  '& h6': {
    fontWeight: 500
  },
  '&.block': {
    border: 'none',
    background: theme.palette.grey[100],
    '&:hover': {
      background: theme.palette.grey[100]
    }
  }
}))

export const LessonBlock = styled('div')({
  textAlign: 'center',
  cursor: 'initial',
  position: 'absolute',
  backgroundColor: 'rgba(250, 250, 250, 0.32)',
  backdropFilter: 'blur(6px)',
  display: 'grid',
  width: '100%',
  borderRadius: '0px 0px 16px 16px',
  top: 0,
  height: '100%',
  '& h6, h5': {
    backgroundImage: 'linear-gradient(89.97deg, #D5B385 -12.76%, #B49568 113.26%)',
    fill: '#B49568',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 500
  }
})

function LessonsBlock({ lesson, vipContent, hasaccess }: LessonTemplateProps) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const dispatch = useDispatch()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { loading, msg, error } = useSelector((state: IRootState) => state.lesson)
  const [anchorOp, setAnchorOp] = useState<null | HTMLElement>(null)
  const [openOptions, setOpenOptions] = useState(false)
  const [openDeleteLesson, setOpenDeleteLesson] = useState(false)
  const [lessonSelected, setLessonSelected] = useState(false)

  const handleCloseOptions = () => {
    setAnchorOp(null)
    setOpenOptions(false)
  }

  const handleClickLesson = () => {
    if (authUser !== null && hasaccess) dispatch(courseLessonStep({ course_id: lesson.course_id, lesson_order: lesson.order }))

    dispatch(setViewLesson(lesson))
  }

  return (
    <>
      <LessonCard
        sx={[
          vipContent && {
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
            marginTop: 4,
            borderRadius: 16,
            '&.active': {
              background: `linear-gradient(#fafafa,#fafafa) padding-box,
            "linear-gradient(89.97deg, #D5B385 -12.76%, #B49568 113.26%)" border-box;`,
              border: '2px solid transparent',
              boxShadow: 'none'
            },
            '& h6': {
              fontWeight: 500
            },
            '&:hover': {
              background: 'rgb(213 179 133 / 23%)'
            }
          }
        ]}
        className={clsx(lesson.lesson_viewed.length > 0 ? 'active' : '', hasaccess ? '' : 'block', 'websiteCourseLesson')}
      >
        <CardContent>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid xs={1} sm={2} onClick={() => handleClickLesson()}>
              <Box width="100%" justifyContent="center" display="flex">
                {lesson.lesson_viewed.length > 0 ? (
                  <ReplayRounded
                    sx={theme => ({
                      color: theme.palette.grey[500],
                      fontSize: '56px !important',
                      '&.live': {
                        color: theme.palette.error.main
                      },
                      '&.new': {
                        color: theme.palette.success.main
                      },
                      [theme.breakpoints.down('sm')]: {
                        fontSize: '32px !important'
                      }
                    })}
                  />
                ) : (
                  <PlayCircleFilled
                    role="button"
                    aria-label="play"
                    sx={theme => ({
                      color: theme.palette.grey[500],
                      fontSize: '56px !important',
                      '&.live': {
                        color: theme.palette.error.main
                      },
                      '&.new': {
                        color: theme.palette.success.main
                      },
                      [theme.breakpoints.down('sm')]: {
                        fontSize: '32px !important'
                      }
                    })}
                  />
                )}
              </Box>
            </Grid>

            <Grid xs={9} onClick={() => handleClickLesson()}>
              <Box pl={isMobile ? 2 : 0}>
                <Typography variant="subtitle1">{lesson.title}</Typography>

                <Box display="flex" sx={{ gap: 1 }} alignItems="center" pt={isMobile ? 0 : 1}>
                  <QueryBuilderRounded fontSize="inherit" />
                  <Typography color="inherit">{lesson.duration} Min</Typography>
                </Box>
                <Box
                  sx={[
                    { gap: 1, display: 'flex', alignItems: 'center' },
                    vipContent
                      ? {
                          backgroundImage: 'linear-gradient(89.97deg, #D5B385 -12.76%, #B49568 113.26%)',
                          fill: '#B49568',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }
                      : { color: 'primary.main' }
                  ]}
                  pt={isMobile ? 0 : 1}
                >
                  {lesson.lesson_viewed.length > 0 ? (
                    <>
                      {vipContent ? <Visibility color="inherit" sx={{ fill: '#BFA75E' }} /> : <Visibility color="inherit" />}
                      <Typography variant="body1" color="inherit">
                        Lección reproducida
                      </Typography>
                    </>
                  ) : (
                    <>
                      {vipContent ? <VisibilityOff color="inherit" sx={{ fill: '#BFA75E' }} /> : <VisibilityOff color="inherit" />}
                      <Typography variant="body1" color="inherit">
                        Por reproducir
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid xs={2} sm={1}>
              {isAdmin(authUser?.role_id) ||
                (isTrainingManager(authUser?.role_id) && (
                  <>
                    <IconButton
                      id={`icon-button-${lesson.id}`}
                      aria-controls="icon-menu"
                      aria-haspopup="false"
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        setAnchorOp(event.currentTarget)
                        setOpenOptions(true)
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      id={`simple-menu-${lesson.id}`}
                      anchorEl={anchorOp}
                      keepMounted
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      open={openOptions}
                      onClick={handleCloseOptions}
                      onClose={handleCloseOptions}
                    >
                      <MenuItem onClick={() => dispatch(setViewLesson(lesson))}>{t('view')}</MenuItem>
                      <MenuItem onClick={() => dispatch(showLesson(lesson.id))}>{t('edit')}</MenuItem>
                      <MenuItem
                        onClick={() => {
                          setOpenDeleteLesson(!openDeleteLesson)
                          setLessonSelected(lesson.id)
                        }}
                      >
                        {t('delete')}
                      </MenuItem>
                    </Menu>
                  </>
                ))}
            </Grid>
          </Grid>
        </CardContent>
        {vipContent && !hasaccess && (
          <LessonBlock>
            <Typography variant="subtitle1" color="inherit">
              Este es un contenido
            </Typography>
            <Typography variant="h5" color="inherit">
              VIP
            </Typography>
          </LessonBlock>
        )}
      </LessonCard>

      <StyledDialog
        open={openDeleteLesson}
        onClose={() => setOpenDeleteLesson(!openDeleteLesson)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Grid container justifyContent="space-between">
            <Typography variant="h6">Eliminar Lección</Typography>
            <Button onClick={() => setOpenDeleteLesson(!openDeleteLesson)} color="primary" variant="text">
              <CloseRounded />
            </Button>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid xs={12}>
              <Typography>¿Está seguro que desea eliminar el lección?</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={() => setOpenDeleteLesson(!openDeleteLesson)}>{t('cancel')}</CancelButton>
          <DeleteButton
            id="btn-handlelogin"
            onClick={() => {
              dispatch(deleteLesson(lessonSelected))
              setOpenDeleteLesson(!openDeleteLesson)
            }}
          >
            {t('delete')}
          </DeleteButton>
        </DialogActions>
      </StyledDialog>

      <CustomSnackBar errors={[error]} msgs={[msg]} />
      <ScreenLoading loadings={[loading]} />
    </>
  )
}

export default LessonsBlock
